import { useCallback, useEffect, useState } from "react";
import { Package, Search } from "lucide-react";
import type { MockProductV2 } from "../pages/v2/mockData";
import { fetchUserProducts } from "../pages/v2/productsApi";
import CachedImg from "./CachedImg";
import ProductGridSkeleton from "./ProductGridSkeleton";
import { AddProductFlow } from "./AddProductFlow";

/**
 * Tab "Mis productos" de la pantalla Mi marca: barra de búsqueda + botón para
 * agregar producto (mismo flujo/UI que seleccionar productos) + grilla de los
 * productos de la marca activa.
 */
export const MiMarcaProductsTab = ({
  companyId,
  brandId,
}: {
  companyId: string | null;
  brandId: string | null;
}) => {
  const [products, setProducts] = useState<MockProductV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    // Sin companyId no hay nada que traer; apagamos el loading para no dejar el
    // skeleton colgado.
    if (!companyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // `fetchUserProducts` maneja ambos casos: con marca activa trae los de esa
      // marca + los sin marca (`brand_id null`); SIN marca activa (user que nunca
      // creó una marca) trae todos los productos de la empresa. Antes esto hacía
      // `return` si `brandId` era null → el user sin marca no veía nada.
      // Ver [[productos-huerfanos-brand-null]].
      const p = await fetchUserProducts(companyId, brandId);
      setProducts(p);
    } finally {
      setLoading(false);
    }
  }, [companyId, brandId]);

  useEffect(() => {
    load();
  }, [load]);

  const q = query.toLowerCase().trim();
  const filtered = q
    ? products.filter((p) => (p.name || "").toLowerCase().includes(q))
    : products;

  return (
    <div>
      {/* Barra de búsqueda + Agregar producto */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1">
          <label htmlFor="mimarca-search-productos" className="sr-only">
            Buscar productos
          </label>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input
            id="mimarca-search-productos"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar productos..."
            className="w-full pl-10 pr-3 h-[var(--btn-h)] rounded-md border border-neutral-200 bg-surface-card text-[length:var(--text-body)] text-neutral-900 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors"
          />
        </div>
        <AddProductFlow
          companyId={companyId}
          brandId={brandId}
          onSaved={load}
          className="w-full sm:w-auto"
        />
      </div>

      {loading ? (
        <ProductGridSkeleton
          count={9}
          gridClassName="grid grid-cols-2 xl:grid-cols-3 gap-3"
          tileClassName="rounded-xl"
          withLabel
        />
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-16 text-brand-black/40">
          <Package className="w-8 h-8 mb-3" strokeWidth={1.5} />
          <p className="text-[14px]">
            {query
              ? "No se encontraron productos."
              : "Todavía no hay productos en esta marca."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((p) => (
            <div key={p.id} className="flex flex-col">
              <div className="relative aspect-square rounded-xl overflow-hidden bg-neutral-100">
                {p.imageUrl ? (
                  <CachedImg
                    src={p.imageUrl}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-brand-black/25">
                    <Package className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                )}
              </div>
              <p className="mt-1.5 text-[12.5px] text-brand-black/80 leading-tight line-clamp-2">
                {p.name}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
